import { useAuth } from '@clerk/react';
import { useEffect } from 'react';
import { setApiAccessToken } from '@client/api';

/**
 * ClerkTokenBridge bridges Clerk's hook-based authentication with Redux-Saga.
 *
 * Since Clerk's getToken() is hook-based and cannot be called from sagas,
 * this component updates the API access token whenever it changes.
 * Sagas then read the token via getApiAccessToken(), keeping saga changes minimal.
 */
export function ClerkTokenBridge() {
  const { getToken, isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    let isMounted = true;

    const updateToken = async () => {
      if (!isLoaded) return;

      if (isSignedIn) {
        try {
          const token = await getToken();
          if (isMounted && token) {
            setApiAccessToken(token);
          }
        } catch (error) {
          console.error('Failed to get Clerk token:', error);
        }
      } else {
        // User is signed out, clear the token
        if (isMounted) {
          setApiAccessToken(null);
        }
      }
    };

    updateToken();

    // Poll for token updates every 5 minutes to handle token refresh
    const interval = setInterval(updateToken, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isLoaded, isSignedIn, getToken]);

  // This component doesn't render anything
  return null;
}
